import type { Id } from "@Aspecto/backend/convex/_generated/dataModel";

import { api } from "@Aspecto/backend/convex/_generated/api";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { CircleStar, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { GroupsPageAdminTab } from "@/components/groups/groups-page-admin-tab";
import { AppPageContent } from "@/components/layout/app-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGroupLabel } from "@/hooks/use-group-label";
import { useCurrentUser } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/groups/")({
  component: GroupsPage,
});

function GroupsPage() {
  const { data: groups, refetch } = useSuspenseQuery(convexQuery(api.groups.getCompanyGroups, {}));
  const { data: currentUser } = useCurrentUser();
  const groupLabel = useGroupLabel();
  const [listTab, setListTab] = useState<"all" | "my" | "admin">("all");
  const [joinDialogGroup, setJoinDialogGroup] = useState<{
    id: Id<"groups">;
    name: string;
    visibility: string;
  } | null>(null);

  const myMemberGroups = useMemo(() => (groups ?? []).filter((g) => g.isMember), [groups]);

  const { mutate: joinGroup, isPending: isJoining } = useMutation({
    mutationFn: useConvexMutation(api.groupMembers.joinGroup),
    onSuccess: () => {
      setJoinDialogGroup(null);
      toast.success(
        "Congratulations, now you are a member and can participate and receive notifications.",
      );
      refetch();
    },
  });

  const { mutate: requestToJoin, isPending: isRequesting } = useMutation({
    mutationFn: useConvexMutation(api.groupJoinRequests.requestToJoinGroup),
    onSuccess: () => {
      setJoinDialogGroup(null);
      toast.success("Your request to join has been sent.");
      refetch();
    },
  });

  const { mutate: acceptInvitation, isPending: isAccepting } = useMutation({
    mutationFn: useConvexMutation(api.groupInvitations.acceptGroupInvitation),
    onSuccess: () => {
      refetch();
    },
  });

  const canCreateGroups =
    currentUser?.role === "company_admin" || currentUser?.role === "rlg_admin";

  const isCompanyAdmin = currentUser?.role === "company_admin" || currentUser?.role === "rlg_admin";
  const isStrictCompanyAdmin = currentUser?.role === "company_admin";

  const handleJoinClick = (
    e: React.MouseEvent,
    group: { _id: Id<"groups">; name: string; visibility: string },
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setJoinDialogGroup({ id: group._id, name: group.name, visibility: group.visibility });
  };

  const handleAcceptInvitation = (e: React.MouseEvent, token: string) => {
    e.preventDefault();
    e.stopPropagation();
    acceptInvitation({ token });
  };

  const renderGroupCards = (list: NonNullable<typeof groups>) => (
    <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {list.map((group) => {
        const isAdmin =
          isCompanyAdmin ||
          group.memberRole === "group_admin" ||
          (group.isGlobal && isCompanyAdmin);

        const actionRow = group.isMember ? (
          <Button variant="outline" className="w-full cursor-not-allowed" disabled>
            {isAdmin ? "Admin" : "Joined"}
          </Button>
        ) : group.hasPendingInvitation && group.invitationToken ? (
          <Button
            className="w-full cursor-pointer bg-foreground text-background hover:bg-foreground/90 disabled:cursor-not-allowed"
            onClick={(e) => handleAcceptInvitation(e, group.invitationToken!)}
            disabled={isAccepting}
          >
            {isAccepting ? "Accepting..." : "Accept Invitation"}
          </Button>
        ) : group.hasPendingJoinRequest ? (
          <Button variant="outline" className="w-full cursor-not-allowed" disabled>
            Request Pending
          </Button>
        ) : !group.isGlobal && group.visibility === "public" ? (
          <Button
            className="w-full cursor-pointer bg-foreground text-background hover:bg-foreground/90"
            onClick={(e) => handleJoinClick(e, group)}
          >
            Join
          </Button>
        ) : !group.isGlobal && group.visibility === "private" ? (
          <Button
            className="w-full cursor-pointer bg-foreground text-background hover:bg-foreground/90"
            onClick={(e) => handleJoinClick(e, group)}
          >
            Request to join
          </Button>
        ) : null;

        return (
          <Link
            key={group._id}
            to="/app/groups/$groupId"
            params={{ groupId: group._id }}
            className="block h-full cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <div className="ring-foreground/10 flex h-full flex-col gap-3 rounded-xl bg-card p-5 text-card-foreground ring-1 shadow-sm transition-all duration-200 hover:bg-accent/50 hover:shadow-md">
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-bold text-foreground">{group.name}</h3>
                {group.description && (
                  <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
                    {group.description}
                  </p>
                )}
              </div>
              <div className="mt-auto flex flex-col gap-2">
                {group.tags && group.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {group.tags.filter(Boolean).map((tag) => (
                      <Badge key={tag!._id} variant="secondary" className="text-xs">
                        {tag!.name}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="min-h-10 w-full">{actionRow}</div>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );

  return (
    <AppPageContent variant="medium">
      <PageHeader
        title={groupLabel.plural}
        description="Manage and track activity across all communities"
        action={
          canCreateGroups ? (
            <Button
              className="cursor-pointer"
              render={<Link to="/app/groups/new" />}
              nativeButton={false}
            >
              <Plus className="h-4 w-4" />
              Create {groupLabel.singular}
            </Button>
          ) : undefined
        }
      />

      {groups?.length === 0 ? (
        <EmptyState
          icon={CircleStar}
          title={`No ${groupLabel.pluralLower} yet`}
          description={`${groupLabel.plural} will appear here once they are created.`}
        />
      ) : (
        <Tabs value={listTab} onValueChange={(v) => setListTab(v as "all" | "my" | "admin")}>
          <TabsList className="mb-6">
            <TabsTrigger value="all" className="cursor-pointer">
              All
            </TabsTrigger>
            <TabsTrigger value="my" className="cursor-pointer">
              My
            </TabsTrigger>
            {isStrictCompanyAdmin ? (
              <TabsTrigger value="admin" className="cursor-pointer">
                Admin
              </TabsTrigger>
            ) : null}
          </TabsList>
          <TabsContent value="all">{renderGroupCards(groups ?? [])}</TabsContent>
          <TabsContent value="my">
            {myMemberGroups.length === 0 ? (
              <EmptyState
                icon={CircleStar}
                title={`No ${groupLabel.pluralLower} in My`}
                description={`Open the All tab to browse ${groupLabel.pluralLower} you can join.`}
              />
            ) : (
              renderGroupCards(myMemberGroups)
            )}
          </TabsContent>
          {isStrictCompanyAdmin ? (
            <TabsContent value="admin">
              <GroupsPageAdminTab />
            </TabsContent>
          ) : null}
        </Tabs>
      )}

      <Dialog open={!!joinDialogGroup} onOpenChange={(open) => !open && setJoinDialogGroup(null)}>
        <DialogContent>
          <DialogHeader>
            <div className="mb-2 flex justify-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <CircleStar className="h-6 w-6 text-primary" />
              </div>
            </div>
            <DialogTitle className="text-center">
              {joinDialogGroup?.visibility === "private"
                ? `Request to join "${joinDialogGroup?.name}"`
                : `Join "${joinDialogGroup?.name}"`}
            </DialogTitle>
            <DialogDescription className="text-center">
              {joinDialogGroup?.visibility === "private"
                ? `This is a private ${groupLabel.singularLower}. Your request will be reviewed by an admin.`
                : `Join this ${groupLabel.singularLower} to participate, post, comment, and receive notifications.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              variant="outline"
              className="cursor-pointer"
              onClick={() => setJoinDialogGroup(null)}
            >
              Cancel
            </Button>
            {joinDialogGroup?.visibility === "private" ? (
              <Button
                className="cursor-pointer disabled:cursor-not-allowed"
                onClick={() => requestToJoin({ groupId: joinDialogGroup.id })}
                disabled={isRequesting}
              >
                {isRequesting ? "Requesting..." : "Request to join"}
              </Button>
            ) : (
              <Button
                className="cursor-pointer disabled:cursor-not-allowed"
                onClick={() => joinDialogGroup && joinGroup({ groupId: joinDialogGroup.id })}
                disabled={isJoining}
              >
                {isJoining ? "Joining..." : "Join"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppPageContent>
  );
}
